import { useCallback, useEffect, useRef, useState } from 'react'
import { completeDeviceFlow, startDeviceFlow, type DeviceFlowStartResponse } from '../../api/authorizationApi'

export type DeviceCodeFlowStatus = 'idle' | 'starting' | 'polling' | 'success' | 'error'

export interface DeviceCodeFlowState {
  readonly accessToken: string | null
  readonly error: string | null
  readonly secondsRemaining: number | null
  readonly status: DeviceCodeFlowStatus
  readonly userCode: string | null
  readonly verificationUri: string | null
}

const initialState: DeviceCodeFlowState = {
  accessToken: null,
  error: null,
  secondsRemaining: null,
  status: 'idle',
  userCode: null,
  verificationUri: null,
}

/**
 * Drives the AWS SSO OAuth Device Authorization Grant flow against the Authorization API
 * (api/authorizationApi.ts), mirroring cdh-data-portal-internal's useDeviceCodeFlow: start the
 * flow, show the user a code + a verification link, then poll until they approve it in another
 * tab (or it times out). The resulting access token is held only in memory (this hook's own
 * state) -- it is never written to localStorage/sessionStorage, since that would make a bearer
 * token readable by any script running on the page (e.g. an XSS payload). Persistence across
 * page reloads instead relies on the Authorization API's own HttpOnly session cookie: see
 * hooks/authorization/useAuthorization.ts, which silently calls refreshSSOAccessToken() on mount
 * before falling back to this flow.
 */
export function useDeviceCodeFlow() {
  const [state, setState] = useState<DeviceCodeFlowState>(initialState)
  const abortRef = useRef(false)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearPendingTimer = useCallback(() => {
    if (pendingTimerRef.current !== null) {
      clearTimeout(pendingTimerRef.current)
      pendingTimerRef.current = null
    }
  }, [])

  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    abortRef.current = true
    clearPendingTimer()
    clearCountdown()
    setState(initialState)
  }, [clearCountdown, clearPendingTimer])

  /**
   * Adopts an access token obtained outside the device-code UI (e.g. a silent refresh via the
   * Authorization API's session cookie -- see useAuthorization.ts) without running the device
   * flow. Kept in memory only, same as the rest of this hook's state.
   */
  const updateAccessToken = useCallback((accessToken: string) => {
    setState((prev) => ({ ...prev, accessToken, status: 'success' }))
  }, [])

  useEffect(
    () => () => {
      abortRef.current = true
      clearPendingTimer()
      clearCountdown()
    },
    [clearCountdown, clearPendingTimer],
  )

  const startFlow = useCallback(async () => {
    abortRef.current = false
    clearPendingTimer()
    clearCountdown()
    setState({ ...initialState, status: 'starting' })

    let startResponse: DeviceFlowStartResponse

    try {
      startResponse = await startDeviceFlow()
    } catch {
      setState((prev) => ({ ...prev, error: 'Failed to start device flow. Please try again.', status: 'error' }))
      return
    }

    if (abortRef.current) {
      return
    }

    const intervalMs = startResponse.interval * 1000
    const expiresAt = Date.now() + startResponse.expires_in * 1000
    const computeSecondsRemaining = () => Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))

    setState({
      accessToken: null,
      error: null,
      secondsRemaining: computeSecondsRemaining(),
      status: 'polling',
      userCode: startResponse.user_code,
      verificationUri: startResponse.verification_uri_complete,
    })

    countdownTimerRef.current = setInterval(() => {
      setState((prev) => ({ ...prev, secondsRemaining: computeSecondsRemaining() }))
    }, 1000)

    while (Date.now() < expiresAt) {
      if (abortRef.current) {
        return
      }

      try {
        const response = await completeDeviceFlow(startResponse.device_code)

        if (response.access_token) {
          clearCountdown()
          setState((prev) => ({ ...prev, accessToken: response.access_token as string, status: 'success' }))
          return
        }
      } catch {
        clearCountdown()
        setState((prev) => ({ ...prev, error: 'Authorization failed. Please try again.', status: 'error' }))
        return
      }

      if (abortRef.current) {
        return
      }

      await new Promise<void>((resolve) => {
        pendingTimerRef.current = setTimeout(() => {
          pendingTimerRef.current = null
          resolve()
        }, intervalMs)
      })
    }

    clearCountdown()
    if (!abortRef.current) {
      setState((prev) => ({ ...prev, error: 'Device flow timed out. Please try again.', status: 'error' }))
    }
  }, [clearCountdown, clearPendingTimer])

  return { ...state, reset, startFlow, updateAccessToken }
}
